import { useMemo } from 'react';
import { Message } from '@/types/message';

export const useMessageGrouping = (messages: Message[]) => {
  // Group messages by thread for UI purposes
  return useMemo(() => {
    // Create a map of thread IDs to versions
    const threadMap = new Map<string, Message[]>();
    
    // Group messages by their original message ID or their own ID if they're original
    messages.forEach(message => {
      const threadId = message.relatedToMessageId || message.id;
      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, []);
      }
      threadMap.get(threadId)?.push(message);
    });
    
    // Sort each thread by version/timestamp
    threadMap.forEach((msgs, threadId) => {
      msgs.sort((a, b) => {
        // If versions are available, use them
        if (a.version !== undefined && b.version !== undefined) {
          return a.version - b.version;
        }
        // Otherwise fall back to timestamp
        return a.timestamp.getTime() - b.timestamp.getTime();
      });
    });
    
    // Create a chronologically ordered array of thread groups
    const orderedThreads: Array<{
      threadId: string;
      messages: Message[];
      latestMessage: Message;
      timestamp: Date;
      hasMultipleVersions: boolean;
    }> = [];
    
    // Process all threads and prepare them for display
    threadMap.forEach((threadMessages, threadId) => {
      // Get the latest message in this thread
      const latestMessage = threadMessages[threadMessages.length - 1];
      
      orderedThreads.push({
        threadId,
        messages: threadMessages,
        latestMessage,
        timestamp: latestMessage.timestamp,
        hasMultipleVersions: threadMessages.length > 1 && 
          threadMessages.some(m => m.relatedToMessageId || m.isRegenerated)
      });
    });
    
    // Sort threads chronologically by their latest message timestamp
    return orderedThreads.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [messages]);
};

export const findLatestAIMessage = (messages: Message[]): Message | undefined => {
  // Get all AI messages
  const aiMessages = messages
    .filter(msg => msg.type === 'ai')
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  
  return aiMessages.length > 0 ? aiMessages[0] : undefined;
};
